var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { forwardRef, useRef, useImperativeHandle } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { Scrollbar } from 'react-scrollbars-custom';
import { prefixClaName, classNames } from './../../common/className';
const defaultScrollableClassName = prefixClaName('scrollbar');
/**
 * The react-scrollbars-custom component default not supports auto hide thumb option,
 * the below implementation from this issue:
 * https://github.com/xobotyi/react-scrollbars-custom/issues/46
 */
const Scrollable = forwardRef(function (props, ref) {
    const { className, children, isShowShadow, trackStyle, thumbStyle } = props, restProps = __rest(props, ["className", "children", "isShowShadow", "trackStyle", "thumbStyle"]);
    const scroller = useRef(null);
    useImperativeHandle(ref, () => scroller.current);
    const [isScrolling, setIsScrolling] = useState(false);
    const [isMouseOver, setIsMouseOver] = useState(false);
    const [scrollTop, setScrollTop] = React.useState(0);
    const isShow = isScrolling || isMouseOver;
    const claNames = classNames(defaultScrollableClassName, className);
    const onScroll = useCallback(({ scrollTop }) => {
        /* istanbul ignore next */
        setScrollTop(scrollTop);
    }, []);
    const onScrollStart = useCallback(() => {
        setIsScrolling(true);
    }, []);
    const onScrollStop = useCallback(() => {
        /* istanbul ignore next */
        setIsScrolling(false);
    }, []);
    const onMouseEnter = useCallback(() => {
        setIsMouseOver(true);
    }, []);
    const onMouseLeave = useCallback(() => {
        setIsMouseOver(false);
    }, []);
    const trackProps = useMemo(() => ({
        renderer: (_a) => {
            var { elementRef, style } = _a, restProps = __rest(_a, ["elementRef", "style"]);
            // [TODO]: I don't know how to code it in a perfect way
            restProps.children.props.style.background = '#bfbfbf66';
            return (React.createElement("span", Object.assign({}, restProps, { ref: elementRef, style: Object.assign(Object.assign(Object.assign({}, style), { borderRadius: 0, opacity: isShow ? 1 : 0, transition: 'opacity 0.4s ease-in-out', background: 'transparent' }), trackStyle) })));
        },
    }), [isShow, trackStyle]);
    const thumbProps = useMemo(() => ({
        renderer: (_a) => {
            var { elementRef, style } = _a, restProps = __rest(_a, ["elementRef", "style"]);
            return (React.createElement("div", Object.assign({}, restProps, { ref: elementRef, style: Object.assign(Object.assign(Object.assign({}, style), { background: 'var(--scrollbarSlider-background)', borderRadius: 0 }), thumbStyle) })));
        },
    }), [isShow, thumbStyle]);
    return (React.createElement(Scrollbar, Object.assign({ className: claNames, ref: scroller, onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave }, restProps, { wrapperProps: {
            renderer: (_a) => {
                var { elementRef, style, key } = _a, restProps = __rest(_a, ["elementRef", "style", "key"]);
                return (React.createElement("div", { key: key },
                    React.createElement("div", Object.assign({}, restProps, { ref: elementRef, style: Object.assign(Object.assign({}, style), { right: 0 }) })),
                    isShowShadow ? (React.createElement("div", { className: classNames('shadow', 'top', scrollTop > 0 && 'active') })) : null));
            },
        }, trackXProps: trackProps, trackYProps: trackProps, thumbXProps: thumbProps, thumbYProps: thumbProps, onScrollStart: onScrollStart, onScrollStop: onScrollStop, onScroll: onScroll, scrollDetectionThreshold: 500 }), children));
});
export { Scrollable };
